import React, { useEffect, useRef, useState } from 'react';
import { Phone, Filter, MapPin, ClipboardList, Calendar, Star, ChevronRight } from 'lucide-react';

const features = [
  {
    icon: Phone,
    title: "Websites That Actually Ring",
    description: "Ditch the fancy brochure. Get a high-speed site built to make the phone ring and get quote requests coming in."
  },
  {
    icon: Filter,
    title: "The \"Tire-Kicker\" Filter",
    description: "Our forms pre-qualify leads so you stop driving across town for low-ballers who can't afford you."
  },
  {
    icon: MapPin,
    title: "Automatic Google Ranking",
    description: "Get your business to show up first when locals search \"Tree Removal\" or \"Stump Grinding\" in your area."
  },
  {
    icon: ClipboardList,
    title: "Job Command Center",
    description: "Track every dollar. Know exactly who has been quoted, who booked, and who still owes you money at a glance."
  },
  {
    icon: Calendar,
    title: "Auto-Scheduling",
    description: "Stop playing phone tag. Let customers book their own estimates on your calendar based on your availability."
  },
  {
    icon: Star,
    title: "Review Generator",
    description: "Automatically text customers after the job is done to get 5-star Google reviews. Build your reputation on autopilot."
  }
];

const FeaturesGrid: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const bookingUrl = "https://api.leadconnectorhq.com/widget/booking/C2VL0S6llGMFg5aY5whk";

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-[#F4E4C1] text-[#0F1419]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-6xl font-black mb-6 uppercase tracking-tighter italic">The Only Tools You Need To Scale</h2>
          <p className="text-xl text-[#4E3629] max-w-2xl mx-auto font-bold uppercase tracking-tight">
            Ditch the 10 separate subscriptions. Here is your entire digital office in one rugged system.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div 
              key={idx} 
              className={`p-10 rounded-[2rem] border-2 border-[#4E3629]/10 bg-white hover:border-[#1A4D2E] hover:shadow-2xl transition-all duration-500 group transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <div className="w-16 h-16 bg-[#1A4D2E] text-white rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl shadow-green-900/10">
                <feature.icon className="w-9 h-9" />
              </div>
              <h3 className="text-2xl font-black mb-4 group-hover:text-[#1A4D2E] transition-colors uppercase italic">{feature.title}</h3>
              <p className="text-[#4E3629] leading-relaxed font-bold opacity-80">{feature.description}</p>
              
              <a 
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-10 inline-flex items-center gap-2 text-[#FF6B35] font-black text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 translate-x-[-10px] group-hover:translate-x-0 transition-all hover:text-[#1A4D2E]"
              >
                LEARN MORE <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;